import React, { RefObject, useEffect, useState } from 'react';
import styled from 'styled-components';

interface Cue {
  start: number;
  end: number;
  text: string;
}

interface Props {
  videoRef: RefObject<HTMLVideoElement>;
  subtitlesSrc: string;
}

const SubtitleContainer = styled.div`
  position: absolute;
  bottom: 5%;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
  user-select: none;
`;

const SubtitleText = styled.span`
  background-color: rgba(0, 0, 0, 0.6);
  padding: 0.25em 0.75em;
  border-radius: 0.5em;
  font-size: 24px;
  color: #ffed00;
  text-align: center;
  font-family: 'Brutal', sans-serif;
  text-shadow: 2px 2px 10px black;
`;

export const StyledSubtitles: React.FC<Props> = ({ videoRef, subtitlesSrc }) => {
  const [cues, setCues] = useState<Cue[]>([]);
  const [currentText, setCurrentText] = useState('');

  useEffect(() => {
    fetch(subtitlesSrc)
      .then((res) => res.text())
      .then((text) => setCues(parseVTT(text)))
      .catch((err) => {
        console.error('Failed to load subtitles:', err);
        setCues([]);
      });
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      const time = video.currentTime;
      const activeCue = cues.find((c) => time >= c.start && time <= c.end);
      setCurrentText(activeCue?.text || '');
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('timeupdate', onTimeUpdate);
  }, [cues, videoRef]);

  return (
    currentText.length > 0 && (
      <SubtitleContainer>
        <SubtitleText>{currentText}</SubtitleText>
      </SubtitleContainer>
    )
  );
};

function parseVTT(vtt: string): Cue[] {
  return vtt
    .split('\n\n')
    .map((block) => {
      const [header, ...lines] = block.trim().split('\n');
      const timeLine = header.includes('-->') ? header : lines.shift()!;
      if (!timeLine) return null;
      const [startStr, endStr] = timeLine.split('-->');
      return {
        start: parseTimestamp(startStr.trim()),
        end: parseTimestamp(endStr.trim()),
        text: lines.join('\n'),
      };
    })
    .filter(Boolean) as Cue[];
}

function parseTimestamp(ts: string): number {
  const clean = ts.replace(',', '.').trim();
  const parts = clean.split(':').map(Number).reverse();

  return (parts?.[0] ?? 0) + (parts?.[1] ?? 0) * 60 + (parts?.[2] ?? 0) * 3600;
}
